import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Flag, Eraser, Send, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";

const LETTERS = "ABCDEF";
const fmt = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const navClass = (isCur, answered, flagged) => {
  if (isCur) return "bg-[#2563EB] text-white border-[#2563EB]";
  if (flagged) return "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]";
  if (answered) return "bg-[#DCFCE7] text-[#166534] border-[#86EFAC]";
  return "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]";
};

const Navigator = ({ questions, answers, flags, current, setCurrent }) => (
  <aside className="lg:col-span-4 bg-white rounded-xl border border-slate-200 p-6 h-fit lg:sticky lg:top-20">
    <div className="small-label">Question Navigator</div>
    <div className="grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-5 gap-2 mt-4">
      {questions.map((q, i) => (
        <button key={q.id} onClick={() => setCurrent(i)} data-testid={`question-nav-item-${i}`}
          className={`h-10 rounded-md border text-sm font-mono font-medium transition-transform hover:scale-105 ${navClass(i === current, answers[q.id] !== undefined, flags[q.id])}`}>
          {i + 1}
        </button>
      ))}
    </div>
    <div className="grid grid-cols-2 gap-2 mt-6 text-xs text-slate-600">
      {[["bg-[#DCFCE7] border-[#86EFAC]", "Answered"], ["bg-[#F1F5F9] border-[#CBD5E1]", "Not answered"],
        ["bg-[#FEF3C7] border-[#FDE68A]", "Marked for review"], ["bg-[#2563EB] border-[#2563EB]", "Current"]].map(([c, l]) => (
        <div key={l} className="flex items-center gap-2"><span className={`h-3 w-3 rounded-sm border ${c}`} />{l}</div>
      ))}
    </div>
    <div className="mt-6 pt-4 border-t border-slate-200 text-sm text-slate-600" data-testid="answered-count">
      Answered <span className="font-mono font-semibold text-slate-900">{Object.keys(answers).length}</span> / {questions.length}
    </div>
  </aside>
);

export default function TestRunner() {
  const { attemptId } = useParams();
  const nav = useNavigate();
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [current, setCurrent] = useState(0);
  const [left, setLeft] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const answersRef = useRef({});
  const offsetRef = useRef(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    api.get(`/student/attempts/${attemptId}/paper`).then(({ data }) => {
      if (data.status !== "in_progress") return nav(`/result/${attemptId}`, { replace: true });
      offsetRef.current = new Date(data.server_now) - Date.now();
      setPaper(data);
      setAnswers(data.answers || {});
      answersRef.current = data.answers || {};
    }).catch((e) => { toast.error(errMsg(e)); nav("/dashboard"); });
  }, [attemptId, nav]);

  const submit = useCallback(async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await api.post(`/student/attempts/${attemptId}/submit`, { answers: answersRef.current });
      if (auto) toast.info("Time's up! Your test was submitted automatically.");
      nav(`/result/${attemptId}`, { replace: true });
    } catch (e) {
      submittedRef.current = false;
      setSubmitting(false);
      toast.error(errMsg(e));
    }
  }, [attemptId, nav]);

  useEffect(() => {
    if (!paper) return;
    const tick = () => {
      const s = Math.max(0, Math.floor((new Date(paper.ends_at) - (Date.now() + offsetRef.current)) / 1000));
      setLeft(s);
      if (s === 0) submit(true);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [paper, submit]);

  const choose = (qid, idx) => {
    const next = { ...answersRef.current };
    if (idx === null) delete next[qid]; else next[qid] = idx;
    answersRef.current = next;
    setAnswers(next);
    api.put(`/student/attempts/${attemptId}/answers`, { answers: next }).catch(() => {});
  };

  if (!paper) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const q = paper.questions[current];
  const total = paper.questions.length;
  const warn = left !== null && left <= 60;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <div className="hidden sm:block"><Logo /></div>
          <div className="min-w-0 sm:border-l sm:border-slate-200 sm:pl-4">
            <div className="font-heading font-semibold truncate" data-testid="test-title">{paper.test.title}</div>
            {paper.test.subject && <div className="text-xs text-slate-500">{paper.test.subject}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div data-testid="test-timer-display"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border font-mono text-base font-semibold tabular-nums ${warn ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B] animate-pulse" : "bg-slate-50 border-slate-200 text-slate-900"}`}>
            <Clock className="h-4 w-4" />{left === null ? "--:--:--" : fmt(left)}
          </div>
          <Button onClick={() => setConfirm(true)} disabled={submitting} data-testid="submit-test-button"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white">
            <Send className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Submit</span>
          </Button>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8 bg-white rounded-xl border border-slate-200 p-6 sm:p-8 flex flex-col min-h-[60vh]">
          <div className="flex items-center justify-between">
            <span className="small-label" data-testid="question-counter">Question {current + 1} of {total}</span>
            {flags[q.id] && <Badge className="bg-[#FEF3C7] text-[#92400E] hover:bg-[#FEF3C7]">Marked for review</Badge>}
          </div>
          <h2 key={q.id} className="fade-up font-heading text-xl sm:text-2xl font-semibold text-slate-900 mt-4 leading-snug whitespace-pre-wrap" data-testid="question-text">{q.text}</h2>
          <div className="mt-8 space-y-3 flex-1">
            {q.options.map((o, i) => {
              const sel = answers[q.id] === i;
              return (
                <button key={i} onClick={() => choose(q.id, i)} data-testid={`question-option-${i}`}
                  className={`w-full text-left flex items-center gap-4 p-4 rounded-lg border-2 transition-colors ${sel ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"}`}>
                  <span className={`h-8 w-8 shrink-0 rounded-md grid place-items-center font-mono text-sm font-semibold ${sel ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-600"}`}>{LETTERS[i]}</span>
                  <span className="text-slate-800">{o}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-8 pt-6 border-t border-slate-200">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setFlags({ ...flags, [q.id]: !flags[q.id] })} data-testid="mark-review-button"
                className="hover:bg-[#FEF3C7] hover:text-[#92400E]"><Flag className="h-4 w-4 mr-1.5" />{flags[q.id] ? "Unmark" : "Mark for review"}</Button>
              <Button variant="ghost" onClick={() => choose(q.id, null)} disabled={answers[q.id] === undefined} data-testid="clear-response-button"
                className="hover:bg-slate-100 hover:text-slate-900"><Eraser className="h-4 w-4 mr-1.5" />Clear</Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCurrent(current - 1)} disabled={current === 0} data-testid="prev-question-button"
                className="hover:bg-slate-900 hover:text-white"><ChevronLeft className="h-4 w-4 mr-1" />Previous</Button>
              {current < total - 1 ? (
                <Button onClick={() => setCurrent(current + 1)} data-testid="next-question-button"
                  className="bg-slate-900 hover:bg-[#2563EB] hover:text-white text-white">Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button onClick={() => setConfirm(true)} data-testid="finish-test-button"
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white text-white">Finish<Send className="h-4 w-4 ml-1.5" /></Button>
              )}
            </div>
          </div>
        </section>
        <Navigator questions={paper.questions} answers={answers} flags={flags} current={current} setCurrent={setCurrent} />
      </main>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="confirm-submit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit test?</AlertDialogTitle>
            <AlertDialogDescription>
              You have answered {Object.keys(answers).length} of {total} questions.
              {total - Object.keys(answers).length > 0 && ` ${total - Object.keys(answers).length} unanswered.`} You can't change answers after submitting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit-dialog-button">Keep working</AlertDialogCancel>
            <AlertDialogAction onClick={() => submit(false)} data-testid="confirm-submit-dialog-button"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">Submit now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
